import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { workItemsApi, type WorkItem, type WorkItemInput } from "../../api/workItemsApi.js";
import { workItemMasterApi } from "../../api/workItemMasterApi.js";
import { WorkItemGroup } from "./WorkItemGroup.js";
import { WorkItemsSummary } from "./WorkItemsSummary.js";
import { WorkItemQuickChips } from "./WorkItemQuickChips.js";
import { WorkItemQuickAddRow } from "./WorkItemQuickAddRow.js";
import { masterToOption } from "./masterOptions.js";
import { toast } from "../Toast.js";

type Props = { caseId: string };

const PROFESSION_ORDER = ["judicial", "administrative", "surveyor", "common", "other"];

export function WorkItemsSection({ caseId }: Props) {
  const qc = useQueryClient();

  // ── データ取得 ─────────────────────────────────────────────────────────────
  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["case", caseId, "work-items"],
    queryFn:  () => workItemsApi.list(caseId),
    enabled:  !!caseId,
  });

  const { data: summary } = useQuery({
    queryKey: ["case", caseId, "work-items", "summary"],
    queryFn:  () => workItemsApi.summary(caseId),
    enabled:  !!caseId,
  });

  // 業務マスタは業種で絞らず全件取得し、検索で当てる（設計 §6.2 / D1 の是正）
  const { data: mastersData } = useQuery({
    queryKey: ["work-item-master"],
    queryFn:  () => workItemMasterApi.list(),
  });

  const items: WorkItem[] = itemsData?.data ?? [];
  const masters = mastersData?.data ?? [];

  const options = useMemo(() => masters.map(masterToOption), [masters]);

  // 自由記述の新規行の既定業種＝案件で直近に使った業種
  const defaultProfession = items[items.length - 1]?.profession ?? "judicial";

  function invalidateWorkItems() {
    qc.invalidateQueries({ queryKey: ["case", caseId, "work-items"] });
    qc.invalidateQueries({ queryKey: ["case", caseId, "work-items", "summary"] });
  }

  // ── ミューテーション ────────────────────────────────────────────────────────

  const addMut = useMutation({
    mutationFn: (item: WorkItemInput) => workItemsApi.create(caseId, item),
    onSuccess: () => {
      invalidateWorkItems();
      toast("業務を追加しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<WorkItemInput> }) =>
      workItemsApi.update(caseId, id, patch),
    onMutate: async ({ id, patch }) => {
      // 楽観更新
      await qc.cancelQueries({ queryKey: ["case", caseId, "work-items"] });
      const prev = qc.getQueryData<{ data: WorkItem[]; total: number }>(
        ["case", caseId, "work-items"],
      );
      if (prev) {
        qc.setQueryData(["case", caseId, "work-items"], {
          ...prev,
          data: prev.data.map((i) =>
            i.id === id
              ? {
                  ...i,
                  ...patch,
                  amount: Math.floor(
                    (patch.quantity ?? i.quantity) * (patch.unit_price ?? i.unit_price),
                  ),
                }
              : i,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["case", caseId, "work-items"], ctx.prev);
      toast("更新に失敗しました", "error");
    },
    onSettled: () => {
      invalidateWorkItems();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => workItemsApi.delete(caseId, id),
    onSuccess: () => {
      invalidateWorkItems();
      toast("業務を削除しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  const duplicateMut = useMutation({
    mutationFn: (item: WorkItem) =>
      workItemsApi.create(caseId, {
        profession:         item.profession,
        category:           item.category,
        title:              item.title + " (複製)",
        description:        item.description ?? undefined,
        quantity:           item.quantity,
        unit_price:         item.unit_price,
        amount:             item.amount,
        tax_category:       item.tax_category,
        withholding_target: item.withholding_target,
        is_pass_through:    item.is_pass_through,
        target_doc_types:   item.target_doc_types ?? undefined,
        sort_order:         item.sort_order + 1,
    }),
    onSuccess: () => {
      invalidateWorkItems();
      toast("業務を複製しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  // ── 業種別グループ化 ────────────────────────────────────────────────────────
  const grouped = PROFESSION_ORDER.reduce<Record<string, WorkItem[]>>((acc, prof) => {
    const profItems = items.filter((i) => i.profession === prof);
    if (profItems.length > 0) acc[prof] = profItems;
    return acc;
  }, {});

  return (
    <div style={{ marginBottom: 32 }}>
      {/* セクションヘッダー */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12,
      }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          実施業務
          <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, marginLeft: 8 }}>
            {items.length}件
          </span>
        </h2>
      </div>

      {/* よく使う業務（クリックで即 1 行追加） */}
      <WorkItemQuickChips caseId={caseId} masters={masters} />

      {/* コンテンツ */}
      {isLoading ? (
        <div style={{ padding: "24px 0", color: "#94a3b8", textAlign: "center" }}>
          読み込み中…
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
          {/* 業種別グループ */}
          {Object.entries(grouped).map(([prof, profItems]) => (
            <WorkItemGroup
              key={prof}
              profession={prof}
              items={profItems}
              onUpdate={(id, patch) => updateMut.mutate({ id, patch })}
              onDelete={(id) => deleteMut.mutate(id)}
              onDuplicate={(item) => duplicateMut.mutate(item)}
            />
          ))}

          {items.length === 0 && (
            <div style={{ fontSize: 12, color: "#94a3b8", padding: "4px 2px" }}>
              下の行に業務名を打って Enter → 金額を打って Enter で、上から続けて入力できます。
            </div>
          )}

          {/* 常設の空行（行内インクリメンタル検索） */}
          <WorkItemQuickAddRow
            caseId={caseId}
            masters={masters}
            options={options}
            defaultProfession={defaultProfession}
            onAdd={(item) => addMut.mutate(item)}
          />

          {/* 集計バー */}
          {summary && items.length > 0 && <WorkItemsSummary summary={summary} />}
        </div>
      )}

    </div>
  );
}
